import { Controller } from "@hotwired/stimulus"
import { debounce, isTopmostElement } from "@/helpers/utils"

export default class extends Controller<HTMLDetailsElement | HTMLDivElement> {
  static targets = ["cta", "summaryContent"]
  declare readonly ctaTarget: HTMLSpanElement
  declare readonly hasCtaTarget: boolean
  declare readonly summaryContentTarget: HTMLElement
  declare readonly hasSummaryContentTarget: boolean

  static values = {
    clamp: { type: Number, default: 0 },
    ctaClose: String,
    ctaOpen: String,
    expandOnce: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    scrollBlock: { type: String, default: "center" }
  }
  declare readonly clampValue: number
  declare readonly ctaCloseValue: string
  declare readonly ctaOpenValue: string
  declare readonly expandOnceValue: boolean
  declare openValue: boolean
  declare readonly scrollBlockValue: "start" | "center"

  connect(): void {
    if (this.hasCtaTarget) {
      this.ctaTarget.textContent ||= this.ctaOpenValue
    }

    this.setSummaryMaxHeight()
    this.toggleCtaVisibility()
    this.toggleCtaVisibility = debounce(this.toggleCtaVisibility.bind(this), 100)
  }

  openValueChanged(): void {
    if (this.element instanceof HTMLDetailsElement) {
      this.element.open = this.openValue
    } else {
      this.element.toggleAttribute("data-open", this.openValue)
    }
  }

  preventDefault(e: Event): void {
    e.preventDefault()
  }

  toggle(e: Event): void {
    e.preventDefault()

    this.openValue = !this.openValue

    this.setSummaryMaxHeight()

    if (this.openValue) {
      this.scrollIntoViewIfNeeded()
    }

    if (this.hasCtaTarget) {
      if (this.expandOnceValue) {
        this.ctaTarget.remove()
      } else {
        this.ctaTarget.textContent = this.openValue ? this.ctaCloseValue : this.ctaOpenValue
      }
    }
  }

  toggleCtaVisibility(): void {
    if (!this.hasCtaTarget || !this.hasSummaryContentTarget) {
      return
    }

    this.ctaTarget.toggleAttribute(
      "hidden",
      this.summaryContentTarget.scrollHeight <= this.summaryContentTarget.clientHeight
    )
  }

  private scrollIntoViewIfNeeded(): void {
    window.requestAnimationFrame(() => {
      if (!isTopmostElement(this.element)) {
        this.element.scrollIntoView({ behavior: "smooth", block: this.scrollBlockValue })
      }
    })
  }

  // For iOS <= 16, which has a bug with line-clamp involving overflow:hidden and nested elements.
  // https://www2.webkit.org/show_bug.cgi?id=255487
  private setSummaryMaxHeight(): void {
    if (!this.hasSummaryContentTarget || this.clampValue === 0) {
      return
    }

    if (this.openValue) {
      this.summaryContentTarget.style.maxHeight = "none"
    } else {
      const summaryStyle = window.getComputedStyle(this.summaryContentTarget)
      const lineHeight = window.parseInt(summaryStyle.getPropertyValue("line-height"))
      this.summaryContentTarget.style.maxHeight = `${this.clampValue * lineHeight}px`
    }
  }
}
